import { useState } from 'react';
import { CUSTOM_COLUMNS, CUSTOM_DATASETS, type CustomDataset, type CustomReportDefinition, type ProfitRowDto } from '@ecom/contracts';
import { useUiStore } from '@/i18n';
import type { MessageKey } from '@/i18n/messages';
import { useT, useStatusLabel } from '@/i18n/use-t';
import { BoxIcon, CartIcon, CoinsIcon } from '@/components/icons';
import { Section, Segmented } from '@/components/page';
import { ReportTable } from '@/components/report-table';
import { Alert, Button, ConfirmDialog, MetricCard, MetricSkeleton, Pill, Select, cx } from '@/components/ui';
import { count, date, money } from '@/lib/format';
import { labelOr } from '@/lib/labels';
import { useVersus } from '@/lib/report-versus';
import { useAuthStore } from '@/stores/auth.store';
import { useReportsStore } from '@/stores/reports.store';

/**
 * Reports v3 (ADR-118, ADR-120, ADR-122): profit and stock value, where
 * orders go and how fast, what comes back, carts left, the team's work, and
 * the report anyone builds from the columns the server knows.
 */

/** A fraction as a percent with one decimal: "47.6%". */
function usePercent() {
  const locale = useUiStore((state) => state.locale);
  return (value: number | null) =>
    value === null ? '—' : new Intl.NumberFormat(locale === 'ar' ? 'ar-SA' : 'en-SA', { style: 'percent', maximumFractionDigits: 1 }).format(value);
}

/** A figure with one decimal, or a dash. */
function useDecimal() {
  const locale = useUiStore((state) => state.locale);
  return (value: number | null) => (value === null ? '—' : new Intl.NumberFormat(locale === 'ar' ? 'ar-SA' : 'en-SA', { maximumFractionDigits: 1 }).format(value));
}

// ─── Profit (ADR-118) ────────────────────────────────────────────────────────

export function ProfitReport() {
  const { profit, loading, backfillCosts } = useReportsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const percent = usePercent();
  const versus = useVersus();
  const owner = useAuthStore((state) => state.user?.role === 'OWNER');
  const [by, setBy] = useState<'product' | 'category' | 'brand' | 'day'>('product');
  const [confirm, setConfirm] = useState(false);
  const [filled, setFilled] = useState<number | null>(null);

  if (loading && !profit) return <MetricSkeleton />;
  if (!profit) return null;

  const rows: ProfitRowDto[] = { product: profit.byProduct, category: profit.byCategory, brand: profit.byBrand, day: profit.byDay }[by];

  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label={t('profitRevenue')} value={money(profit.totals.revenue, locale)} trend={versus(profit.totals.revenue, profit.previous.revenue)} tone="sky" icon={<CoinsIcon />} />
        <MetricCard label={t('profitCost')} value={money(profit.totals.cost, locale)} trend={versus(profit.totals.cost, profit.previous.cost)} tone="amber" icon={<BoxIcon />} />
        <MetricCard label={t('grossProfit')} value={money(profit.totals.grossProfit, locale)} trend={versus(profit.totals.grossProfit, profit.previous.grossProfit)} tone="mint" icon={<CoinsIcon />} />
        <MetricCard label={t('profitMargin')} value={percent(profit.totals.margin)} hint={t('profitMarginHint')} tone="violet" icon={<CoinsIcon />} />
      </div>

      {profit.withoutCost.lines > 0 ? (
        <Alert tone="info">
          <span>{t('profitWithoutCost', { lines: count(profit.withoutCost.lines, locale), revenue: money(profit.withoutCost.revenue, locale) })}</span>
          {owner ? (
            <Button variant="ghost" size="sm" className="ms-2" onClick={() => setConfirm(true)}>
              {t('profitBackfill')}
            </Button>
          ) : null}
        </Alert>
      ) : null}
      {filled !== null ? <Alert tone="success">{t('profitBackfilled', { lines: count(filled, locale) })}</Alert> : null}

      <Section
        title={t('profitBreakdown')}
        subtitle={t('profitBreakdownHint')}
        action={
          <Segmented
            label={t('profitBreakdown')}
            value={by}
            onChange={setBy}
            items={(['product', 'category', 'brand', 'day'] as const).map((id) => ({ id, label: t(`profitBy_${id}` as MessageKey) }))}
          />
        }
        flush
      >
        <ReportTable
          headers={[t(`profitBy_${by}` as MessageKey), t('unitsSold'), t('profitRevenue'), t('profitCost'), t('grossProfit'), t('profitMargin')]}
          rows={rows.map((row) => [
            <span key="label" className="flex items-center gap-2">
              {by === 'day' ? date(row.key, locale) : row.label || t('profitNone')}
              {row.unitsWithoutCost > 0 ? <Pill tone="warning">{t('profitUnitsWithoutCost', { units: count(row.unitsWithoutCost, locale) })}</Pill> : null}
            </span>,
            count(row.units, locale),
            money(row.revenue, locale),
            money(row.cost, locale),
            <span key="profit" className={cx(row.grossProfit < 0 && 'text-danger')}>
              {money(row.grossProfit, locale)}
            </span>,
            percent(row.margin),
          ])}
          empty={t('noDataForRange')}
        />
      </Section>

      <ConfirmDialog
        open={confirm}
        title={t('profitBackfill')}
        body={t('profitBackfillBody')}
        confirmLabel={t('profitBackfill')}
        cancelLabel={t('cancel')}
        onCancel={() => setConfirm(false)}
        onConfirm={async () => {
          setConfirm(false);
          setFilled(await backfillCosts());
        }}
      />
    </div>
  );
}

export function StockValueReport() {
  const { stock, loading } = useReportsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const [by, setBy] = useState<'product' | 'category' | 'location'>('product');

  if (loading && !stock) return <MetricSkeleton />;
  if (!stock) return null;
  const rows = { product: stock.topProducts, category: stock.byCategory, location: stock.byLocation }[by];

  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label={t('stockUnits')} value={count(stock.totals.units, locale)} tone="sky" icon={<BoxIcon />} />
        <MetricCard label={t('stockAtCost')} value={money(stock.totals.costValue, locale)} tone="amber" icon={<CoinsIcon />} />
        <MetricCard label={t('stockAtPrice')} value={money(stock.totals.retailValue, locale)} hint={t('stockAtPriceHint')} tone="violet" icon={<CoinsIcon />} />
        <MetricCard label={t('stockPotential')} value={money(stock.totals.potentialProfit, locale)} tone="mint" icon={<CoinsIcon />} />
      </div>
      {stock.totals.variantsWithoutCost > 0 ? <Alert tone="info">{t('stockWithoutCost', { variants: count(stock.totals.variantsWithoutCost, locale) })}</Alert> : null}
      <Section
        title={t('stockBreakdown')}
        subtitle={t('stockBreakdownHint')}
        action={<Segmented label={t('stockBreakdown')} value={by} onChange={setBy} items={(['product', 'category', 'location'] as const).map((id) => ({ id, label: t(`stockBy_${id}` as MessageKey) }))} />}
        flush
      >
        <ReportTable
          headers={[t(`stockBy_${by}` as MessageKey), t('stockUnits'), t('stockAtCost'), t('stockAtPrice')]}
          rows={rows.map((row) => [row.label || t('profitNone'), count(row.units, locale), money(row.costValue, locale), money(row.retailValue, locale)])}
          empty={t('stockEmpty')}
        />
      </Section>
    </div>
  );
}

// ─── Operations (ADR-120) ────────────────────────────────────────────────────

export function GeographyReport() {
  const { geography, loading } = useReportsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const percent = usePercent();
  const decimal = useDecimal();
  const [by, setBy] = useState<'region' | 'city'>('city');

  if (loading && !geography) return <MetricSkeleton />;
  if (!geography) return null;
  const rows = by === 'city' ? geography.byCity : geography.byRegion;

  return (
    <Section
      title={t('geographyTitle')}
      subtitle={t('geographyHint')}
      action={<Segmented label={t('geographyTitle')} value={by} onChange={setBy} items={(['city', 'region'] as const).map((id) => ({ id, label: t(`reportFilter_${id}` as MessageKey) }))} />}
      flush
    >
      <ReportTable
        headers={[t(`reportFilter_${by}` as MessageKey), t('orderCount'), t('revenue'), t('averageOrderValue'), t('geographyBuyers'), t('geographyCodShare'), t('geographyCancelled'), t('geographyCodCancelled'), t('geographyDeliveryDays')]}
        rows={rows.map((row) => [
          by === 'city' ? `${row.city} · ${row.region}` : row.region,
          count(row.orders, locale),
          money(row.revenue, locale),
          money(row.averageOrder, locale),
          count(row.buyers, locale),
          percent(row.codShare),
          percent(row.cancelledShare),
          <span key="cod" className={cx((row.codCancelledShare ?? 0) >= 0.2 && 'font-semibold text-danger')}>
            {percent(row.codCancelledShare)}
          </span>,
          decimal(row.deliveryDays),
        ])}
        empty={t('noDataForRange')}
      />
    </Section>
  );
}

export function ShippingReport() {
  const { shipping, loading } = useReportsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const decimal = useDecimal();

  if (loading && !shipping) return <MetricSkeleton />;
  if (!shipping) return null;

  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label={t('shippingShipments')} value={count(shipping.totals.shipments, locale)} tone="sky" icon={<BoxIcon />} />
        <MetricCard label={t('shippingHoursToShip')} value={decimal(shipping.totals.hoursToShip)} hint={t('shippingHoursHint')} tone="amber" icon={<BoxIcon />} />
        <MetricCard label={t('shippingDaysInTransit')} value={decimal(shipping.totals.daysInTransit)} tone="violet" icon={<BoxIcon />} />
        <MetricCard label={t('shippingCharged')} value={money(shipping.totals.charged, locale)} hint={t('shippingFreeOrders', { count: count(shipping.totals.freeOrders, locale) })} tone="mint" icon={<CoinsIcon />} />
      </div>
      <Section title={t('shippingByCarrier')} flush>
        <ReportTable
          headers={[t('shippingCarrier'), t('shippingShipments'), t('shippingDelivered'), t('shippingFailed'), t('shippingInTransit'), t('shippingHoursToShip'), t('shippingDaysInTransit')]}
          rows={shipping.byCarrier.map((row) => [row.carrier, count(row.shipments, locale), count(row.delivered, locale), count(row.failed, locale), count(row.inTransit, locale), decimal(row.hoursToShip), decimal(row.daysInTransit)])}
          empty={t('noDataForRange')}
        />
      </Section>
      <Section title={t('shippingByMethod')} flush>
        <ReportTable
          headers={[t('shippingMethod'), t('orderCount'), t('shippingCharged'), t('shippingAverage'), t('shippingFree')]}
          rows={shipping.byMethod.map((row) => [row.pickup ? `${row.method} · ${t('reportFulfilment_pickup')}` : row.method, count(row.orders, locale), money(row.charged, locale), money(row.averageCharged, locale), count(row.freeOrders, locale)])}
          empty={t('noDataForRange')}
        />
      </Section>
    </div>
  );
}

export function ReturnsReport() {
  const { returns, loading } = useReportsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const percent = usePercent();
  const decimal = useDecimal();

  if (loading && !returns) return <MetricSkeleton />;
  if (!returns) return null;
  const reason = (value: string | null) => (value ? labelOr(t, `reason${value}`, value) : '—');

  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label={t('returnsRate')} value={percent(returns.totals.rate)} hint={t('returnsRateHint', { returned: count(returns.totals.unitsReturned, locale), sold: count(returns.totals.unitsSold, locale) })} tone="amber" icon={<BoxIcon />} />
        <MetricCard label={t('returnsCount')} value={count(returns.totals.returns, locale)} tone="sky" icon={<BoxIcon />} />
        <MetricCard label={t('returnsRefunded')} value={money(returns.totals.refunded, locale)} tone="violet" icon={<CoinsIcon />} />
        <MetricCard label={t('returnsDaysToRefund')} value={decimal(returns.totals.daysToRefund)} hint={t('returnsRestocked', { units: count(returns.totals.restockedUnits, locale) })} tone="mint" icon={<CoinsIcon />} />
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        <Section title={t('returnsByReason')} flush>
          <ReportTable headers={[t('returnsReason'), t('returnsCount'), t('unitsSold'), t('returnsRefunded')]} rows={returns.byReason.map((row) => [reason(row.reason), count(row.returns, locale), count(row.units, locale), money(row.refunded, locale)])} empty={t('noDataForRange')} />
        </Section>
        <Section title={t('returnsByResolution')} flush>
          <ReportTable headers={[t('returnsResolution'), t('returnsCount'), t('unitsSold')]} rows={returns.byResolution.map((row) => [labelOr(t, `returnResolution_${row.resolution}`, row.resolution), count(row.returns, locale), count(row.units, locale)])} empty={t('noDataForRange')} />
        </Section>
      </div>
      <Section title={t('returnsByProduct')} subtitle={t('returnsByProductHint')} flush>
        <ReportTable
          headers={[t('product'), t('unitsSold'), t('returnsUnits'), t('returnsRate'), t('returnsRefunded'), t('returnsTopReason')]}
          rows={returns.byProduct.map((row) => [
            <span key="name">
              {row.name} <span className="text-xs text-ink-faint" dir="ltr">{row.sku}</span>
            </span>,
            count(row.unitsSold, locale),
            count(row.unitsReturned, locale),
            <span key="rate" className={cx((row.rate ?? 0) >= 0.1 && 'font-semibold text-danger')}>
              {percent(row.rate)}
            </span>,
            money(row.refunded, locale),
            reason(row.topReason),
          ])}
          empty={t('returnsNone')}
        />
      </Section>
    </div>
  );
}

export function CartsReport() {
  const { carts, loading } = useReportsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const percent = usePercent();
  const decimal = useDecimal();

  if (loading && !carts) return <MetricSkeleton />;
  if (!carts) return null;

  return (
    <div className="flex flex-col gap-5">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard label={t('cartsLeft')} value={count(carts.totals.left, locale)} hint={money(carts.totals.leftValue, locale)} tone="amber" icon={<CartIcon />} />
        <MetricCard label={t('cartsRecovered')} value={count(carts.totals.recovered, locale)} hint={percent(carts.totals.recoveryRate)} tone="mint" icon={<CartIcon />} />
        <MetricCard label={t('cartsRecoveredRevenue')} value={money(carts.totals.recoveredRevenue, locale)} tone="sky" icon={<CoinsIcon />} />
        <MetricCard label={t('cartsHoursToRecover')} value={decimal(carts.totals.hoursToRecover)} tone="violet" icon={<CartIcon />} />
      </div>
      <Section title={t('cartsByReminder')} subtitle={t('cartsByReminderHint')} flush>
        <ReportTable
          headers={[t('cartsReminders'), t('cartsLeft'), t('cartsRecovered'), t('cartsRate'), t('cartsRecoveredRevenue')]}
          rows={carts.byReminder.map((row) => [t(`cartsReminder_${row.reminders}` as MessageKey), count(row.left, locale), count(row.recovered, locale), percent(row.rate), money(row.recoveredRevenue, locale)])}
          empty={t('noDataForRange')}
        />
      </Section>
      <Section title={t('byDay')} flush>
        <ReportTable
          headers={[t('date'), t('cartsLeft'), t('cartsRecovered'), t('cartsValueLeft'), t('cartsRecoveredRevenue')]}
          rows={[...carts.byDay].reverse().map((row) => [date(row.date, locale), count(row.left, locale), count(row.recovered, locale), money(row.leftValue, locale), money(row.recoveredRevenue, locale)])}
          empty={t('noDataForRange')}
        />
      </Section>
    </div>
  );
}

export function StaffReport() {
  const { staff, loading } = useReportsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  if (loading && !staff) return <MetricSkeleton />;
  if (!staff) return null;
  const minutes = (value: number | null) => (value === null ? '—' : t('staffMinutes', { minutes: count(value, locale) }));

  return (
    <Section title={t('staffTitle')} subtitle={t('staffHint')} flush>
      <ReportTable
        headers={[t('staffMember'), t('staffMoved'), t('staffShipped'), t('staffAssigned'), t('staffReplies'), t('staffFirstReply'), t('staffFollowUps'), t('staffNotes')]}
        rows={staff.rows.map((row) => [
          row.name,
          count(row.statusChanges, locale),
          count(row.shipped, locale),
          count(row.assigned, locale),
          count(row.ticketReplies, locale),
          row.firstReplies > 0 ? `${minutes(row.firstReplyMinutes)} (${count(row.firstReplies, locale)})` : '—',
          count(row.followUpsDone, locale),
          count(row.notes, locale),
        ])}
        empty={t('noDataForRange')}
      />
    </Section>
  );
}

// ─── Custom reports (ADR-122) ────────────────────────────────────────────────

export function CustomReport() {
  const { custom, customResult, loading, setCustom, load, exportCustom, exporting } = useReportsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const percent = usePercent();
  const statusLabel = useStatusLabel();
  const specs = CUSTOM_COLUMNS[custom.dataset];
  const kindOf = (key: string) => specs.find((spec) => spec.key === key);
  const label = (key: string) => t(`customColumn_${key}` as MessageKey);

  const update = (patch: Partial<CustomReportDefinition>) => {
    const next = { ...custom, ...patch };
    // Grouping keeps the report honest: a grouped report shows its groupings and measures.
    if (next.groupBy.length > 0) next.columns = next.columns.filter((key) => kindOf(key)?.kind === 'measure' || next.groupBy.includes(key));
    for (const key of next.groupBy) if (!next.columns.includes(key)) next.columns = [key, ...next.columns];
    if (next.sort && !next.columns.includes(next.sort.column)) next.sort = null;
    setCustom(next);
  };
  const toggle = (key: string) => update({ columns: custom.columns.includes(key) ? custom.columns.filter((entry) => entry !== key) : [...custom.columns, key] });
  const grouped = custom.groupBy.length > 0;
  const cell = (key: string, value: string | number | null, type: string) => {
    if (value === null) return '—';
    if (type === 'money') return money(Number(value), locale);
    if (type === 'percent') return percent(Number(value));
    if (type === 'number') return count(Number(value), locale);
    if (type === 'date' && typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) return date(value.slice(0, 10), locale);
    if (key === 'status') return statusLabel(String(value));
    if (key === 'payment') return labelOr(t, `orderMethod${value}`, String(value));
    if (key === 'channel') return labelOr(t, `channel_${value}`, String(value));
    if (key === 'customerType') return labelOr(t, `reportCustomer_${value}`, String(value));
    return String(value);
  };

  return (
    <div className="flex flex-col gap-5">
      <Section title={t('customTitle')} subtitle={t('customHint')}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <Segmented
              label={t('customDataset')}
              value={custom.dataset}
              onChange={(dataset: CustomDataset) => setCustom({ dataset, columns: CUSTOM_COLUMNS[dataset].filter((spec) => spec.kind === 'measure').slice(0, 3).map((spec) => spec.key), groupBy: [], sort: null })}
              items={CUSTOM_DATASETS.map((id) => ({ id, label: t(`customDataset_${id}` as MessageKey) }))}
            />
            <label className="flex items-center gap-2 text-xs text-ink-muted">
              {t('customGroupBy')}
              <Select value={custom.groupBy[0] ?? ''} onChange={(event) => update({ groupBy: event.target.value ? [event.target.value, ...custom.groupBy.slice(1).filter((key) => key !== event.target.value)] : [] })} className="w-44">
                <option value="">{t('customNoGrouping')}</option>
                {specs.filter((spec) => spec.groupable).map((spec) => (
                  <option key={spec.key} value={spec.key}>
                    {label(spec.key)}
                  </option>
                ))}
              </Select>
            </label>
            {grouped ? (
              <label className="flex items-center gap-2 text-xs text-ink-muted">
                {t('customThenBy')}
                <Select value={custom.groupBy[1] ?? ''} onChange={(event) => update({ groupBy: event.target.value ? [custom.groupBy[0]!, event.target.value] : custom.groupBy.slice(0, 1) })} className="w-44">
                  <option value="">{t('customNoGrouping')}</option>
                  {specs.filter((spec) => spec.groupable && spec.key !== custom.groupBy[0]).map((spec) => (
                    <option key={spec.key} value={spec.key}>
                      {label(spec.key)}
                    </option>
                  ))}
                </Select>
              </label>
            ) : null}
            <label className="flex items-center gap-2 text-xs text-ink-muted">
              {t('customSortBy')}
              <Select
                value={custom.sort ? `${custom.sort.column}:${custom.sort.direction}` : ''}
                onChange={(event) => {
                  const [column, direction] = event.target.value.split(':');
                  update({ sort: column ? { column, direction: direction === 'asc' ? 'asc' : 'desc' } : null });
                }}
                className="w-48"
              >
                <option value="">—</option>
                {custom.columns.flatMap((key) => [
                  <option key={`${key}:desc`} value={`${key}:desc`}>{`${label(key)} ↓`}</option>,
                  <option key={`${key}:asc`} value={`${key}:asc`}>{`${label(key)} ↑`}</option>,
                ])}
              </Select>
            </label>
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium text-ink-muted">{t('customColumns')}</span>
            <div className="flex flex-wrap gap-1.5">
              {specs
                .filter((spec) => !grouped || spec.kind === 'measure' || custom.groupBy.includes(spec.key))
                .map((spec) => {
                  const on = custom.columns.includes(spec.key);
                  return (
                    <button
                      key={spec.key}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggle(spec.key)}
                      className={cx(
                        'rounded-full border px-3 py-1 text-xs transition-colors',
                        on ? 'border-brand-600 bg-brand-600 text-brand-ink' : 'border-line text-ink-muted hover:border-brand-300 hover:text-ink',
                        spec.kind === 'measure' && !on && 'border-dashed',
                      )}
                    >
                      {label(spec.key)}
                    </button>
                  );
                })}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button onClick={() => void load('custom')} disabled={loading || custom.columns.length === 0}>
              {loading ? t('loading') : t('customRun')}
            </Button>
            {(['xlsx', 'csv', 'pdf'] as const).map((format) => (
              <Button key={format} variant="secondary" size="sm" disabled={exporting.includes(`custom.${format}`)} onClick={() => void exportCustom(format)}>
                {t(format === 'xlsx' ? 'exportExcel' : format === 'pdf' ? 'exportPdf' : 'exportCsv')}
              </Button>
            ))}
          </div>
        </div>
      </Section>

      {customResult ? (
        <Section title={t('customResult')} subtitle={customResult.truncated ? t('customTruncated') : undefined} flush>
          <ReportTable
            headers={customResult.columns.map((column) => label(column.key))}
            rows={customResult.rows.map((row) => row.map((value, index) => cell(customResult.columns[index]!.key, value, customResult.columns[index]!.type)))}
            empty={t('noDataForRange')}
          />
        </Section>
      ) : null}
    </div>
  );
}
